import type { EmailCampaign } from '../types/data';

// GrapesJS stores MJML source; its raw_html starts with an <mjml> root.
const MJML_SIGNATURE = /<mjml[\s>]/i;

/**
 * Resolve which editor an email should open in.
 *
 * Prefer the stored editor_type. When it is missing (legacy campaigns, or a create path
 * that never persisted one), only MJML is inferred: it is unambiguously GrapesJS source,
 * and opening it in Stripo renders the markup as text.
 *
 * Everything else keeps the Stripo default it has always had. Workflow AI drafts do load
 * into Stripo without draggable blocks (SUPPORT-91), but the answer there is to generate
 * HTML Stripo can structure, not to route the leader into a code editor and take the
 * visual builder away.
 */
export function resolveEditorType(
  editorType: string | null | undefined,
  rawHtml: string | null | undefined
): EmailCampaign['editorType'] {
  if (editorType) return editorType as EmailCampaign['editorType'];
  if (!rawHtml) return 'stripo';
  if (MJML_SIGNATURE.test(rawHtml)) return 'grapesjs';
  return 'stripo';
}

export function emailCampaignMapper(emailCampaign): EmailCampaign {
  return {
    id: emailCampaign.id,
    name: emailCampaign.name,
    status: emailCampaign.status,
    failedReason: emailCampaign?.failed_reason ?? null,
    automationType: emailCampaign.CampaignAutomation?.handle ?? null,
    automationCampaignActive: emailCampaign.automation_campaign_active ?? false,
    campaignAutomation: emailCampaign.CampaignAutomation ?? null,
    subjectText: emailCampaign.subject_text,
    previewText: emailCampaign.preview_text,
    scheduledAt: emailCampaign.scheduled_at,
    scheduleTimezone: emailCampaign.schedule_timezone ?? null,
    rawHtml: emailCampaign.raw_html,
    rawCss: emailCampaign.raw_css,
    compiledHtmlCss: emailCampaign.compiled_html_css,
    previewImgUrl: emailCampaign.preview_img_url,
    htmlCssUpdatedAt: emailCampaign.html_css_updated_at,
    editorType: resolveEditorType(emailCampaign.editor_type, emailCampaign.raw_html),
    // `?? null`, never `|| 0`. A campaign whose metrics have never synced has null
    // counters, and 193 sent campaigns in production are in that state. Coercing them to
    // 0 makes "we do not know yet" indistinguishable from "this reached nobody", which
    // reads as total failure. Consumers decide how to render the absence.
    sendCount: emailCampaign.send_count ?? null,
    openCount: emailCampaign.open_count ?? null,
    clickCount: emailCampaign.click_count ?? null,
    unsubscribeCount: emailCampaign.unsubscribe_count ?? null,
    spamReportCount: emailCampaign.spam_report_count ?? null,
    bounceCount: emailCampaign.bounce_count ?? null,
    metricsLastSync: emailCampaign.metrics_last_sync ?? null,
    audiences: emailCampaign.audiences || [],
    senderId: emailCampaign?.Sender?.id || null,
    sender: emailCampaign?.Sender || null,
    unsubscribeGroupId: emailCampaign.unsubscribe_group_id ?? null,
    leadSegmentId: emailCampaign?.LeadSegment?.id || null,
    hasParentCampaign: emailCampaign.recurring_campaign_id != null || emailCampaign.warmup_campaign_id != null,
  };
}
